import {
  Component,
  inject,
  signal,
  computed,
  effect,
  input,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  Validators,
  FormArray,
  FormGroup,
} from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import {
  VOneInputComponent,
  VOneButtonComponent,
  VOnePaginatorComponent,
  VOneSearchComponent,
  InventoryArticleWarningComponent,
} from '@shared-components';
import { STRING_CONSTANTS } from '@shared-constants';
import { IconType } from '@shared-enums';
import {
  Invoice,
  NoteType,
  Article,
  AutocompleteOption,
  SelectOption,
} from '@shared-models';
import { CompanyFeaturesService, DianService } from '@shared-services';

import { INotas } from 'src/app/@interfaces/notas.interface';
import { InventoryService } from 'src/app/@pages/inventory/inventory.service';
import { BillyNotesService } from 'src/app/@shared/components/dialog/fact-notes/billyNotes.service';
import { CreateNoteService } from 'src/app/@shared/services/note/create-note.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

import { NoteArticleType } from '../../enums/note-article-type.enum';
import { NoteConceptCode } from '../../enums/note-concept-code.enum';
import { NoteItemForm } from '../../models/note-item-form.model';
import { NoteBuilderService } from '../../services/note-builder.service';
import { NoteFormCalculationService } from '../../services/note-form-calculation.service';
import { NoteFormLockingService } from '../../services/note-form-locking.service';
import { NoteFormManagerService } from '../../services/note-form-manager.service';
import { NoteFormStateService } from '../../services/note-form-state.service';
import { NoteValidationService } from '../../services/note-validation.service';

@Component({
  selector: 'v-one-new-note',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    VOneInputComponent,
    VOneButtonComponent,
    VOnePaginatorComponent,
    VOneSearchComponent,
    InventoryArticleWarningComponent,
  ],
  providers: [
    NoteFormCalculationService,
    NoteFormLockingService,
    NoteBuilderService,
    NoteFormStateService,
    NoteFormManagerService,
    NoteValidationService,
  ],
  templateUrl: './new-note.component.html',
  styleUrls: ['./new-note.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewNoteComponent {
  // Services
  private readonly dianService = inject(DianService);
  private readonly inventoryService = inject(InventoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly spinnerService = inject(NgxSpinnerService);
  private readonly billyNoteService = inject(BillyNotesService);
  private readonly cookieAuthService = inject(CookieAuthService);
  private readonly createNoteService = inject(CreateNoteService);
  private readonly translate = inject(TranslateService);

  // Feature services
  readonly calculationService = inject(NoteFormCalculationService);
  private readonly lockingService = inject(NoteFormLockingService);
  private readonly builderService = inject(NoteBuilderService);
  readonly stateService = inject(NoteFormStateService);
  private readonly formManagerService = inject(NoteFormManagerService);
  private readonly validationService = inject(NoteValidationService);
  private readonly companyFeaturesService = inject(CompanyFeaturesService);

  private lockingSubscriptions: Subscription[] = [];

  // Constants
  readonly ICON_TYPE = IconType;
  readonly NOTE_ARTICLE_TYPE = NoteArticleType;
  readonly CANCELLATION_CONCEPT_CODE = '2';

  // Inputs
  readonly invoice = input<Invoice>();
  readonly noteType = input<NoteType>();
  readonly totalAccredited = input<number>();
  readonly closeModal = input<() => void>();

  // Form Controls
  readonly articleSearchControl = new FormControl<string | Article>('');
  readonly conceptControl = new FormControl<string>('', {
    validators: [Validators.required],
    nonNullable: true,
  });
  readonly typeControl = new FormControl<number>(NoteArticleType.BY_ARTICLE, {
    validators: [Validators.required],
    nonNullable: true,
  });
  readonly itemsForm = new FormArray<FormGroup<NoteItemForm>>([]);

  // State Service
  readonly conceptOptions = this.stateService.conceptOptions;
  readonly searchValue = this.stateService.searchValue;
  readonly selectedConcept = this.stateService.selectedConcept;
  readonly selectedArticleType = this.stateService.selectedArticleType;
  readonly currentPage = this.stateService.currentPage;
  readonly isLoadingConcepts = this.stateService.isLoadingConcepts;
  readonly searchedArticles = this.stateService.searchedArticles;
  readonly selectedArticle = this.stateService.selectedArticle;
  readonly formArrayVersion = this.stateService.formArrayVersion;

  // Base computed
  readonly sales = computed(() => this.invoice()?.venta || []);
  readonly taxIncluded = computed(
    () => this.invoice()?.tax_incl === STRING_CONSTANTS.TRUE,
  );
  readonly showArticleSearch = computed(
    () => this.selectedArticleType() === NoteArticleType.BY_TOTAL,
  );

  // Pagination & filtering
  readonly filteredSales = computed(() => {
    const search = this.searchValue().toLowerCase();
    if (!search) return this.sales();

    return this.sales().filter((sale) => {
      const article = sale.articulo[0];
      const name = article?.descripcion?.toLowerCase() || '';
      const code = article?.codigo?.toLowerCase() || '';
      return name.includes(search) || code.includes(search);
    });
  });

  readonly totalItems = computed(() => this.filteredSales().length);
  readonly currentSale = computed(
    () => this.filteredSales()[this.currentPage()],
  );

  // Single source of truth for current item index
  private getRealIndexForCurrentPage(): number {
    if (this.selectedArticleType() === NoteArticleType.BY_TOTAL) return 0;

    const filtered = this.filteredSales();
    const pageIndex = this.currentPage();
    const saleOnPage = filtered[pageIndex];
    if (!saleOnPage) return -1;

    return this.sales().indexOf(saleOnPage);
  }

  // Current item group (used by both desktop and mobile)
  readonly currentItemGroup = computed<FormGroup<NoteItemForm> | null>(() => {
    const realIndex = this.getRealIndexForCurrentPage();
    return realIndex >= 0 && realIndex < this.itemsForm.length
      ? this.itemsForm.at(realIndex)
      : null;
  });

  readonly quantityControl = computed(
    () => this.currentItemGroup()?.controls.quantity,
  );
  readonly priceControl = computed(
    () => this.currentItemGroup()?.controls.price,
  );
  readonly subtotalControl = computed(
    () => this.currentItemGroup()?.controls.subtotal,
  );
  readonly totalWithTaxControl = computed(
    () => this.currentItemGroup()?.controls.totalWithTax,
  );

  readonly quantity = computed(() => this.quantityControl()?.value ?? 0);
  readonly price = computed(() => this.priceControl()?.value ?? 0);

  // Mobile getters (same logic as currentItemGroup)
  get mobileItemGroup(): FormGroup<NoteItemForm> | null {
    return this.currentItemGroup();
  }

  get mobileQuantityCtrl(): FormControl<number | null> | null {
    const group = this.mobileItemGroup;
    return group
      ? (group.controls.quantity as FormControl<number | null>)
      : null;
  }

  get mobilePriceCtrl(): FormControl<number | null> | null {
    const group = this.mobileItemGroup;
    return group ? (group.controls.price as FormControl<number | null>) : null;
  }

  // Mobile totals
  readonly itemSubtotal = computed(() => {
    this.formArrayVersion();
    const group = this.mobileItemGroup;
    return group ? Number(group.controls.subtotal.value) || 0 : 0;
  });

  readonly itemTotalWithTax = computed(() => {
    this.formArrayVersion();
    const group = this.mobileItemGroup;
    return group ? Number(group.controls.totalWithTax.value) || 0 : 0;
  });

  // Article info
  readonly articleAutocompleteOptions = computed(() =>
    this.searchedArticles().map(
      (article): AutocompleteOption<Article> => ({
        value: article,
        label: article.descripcion,
      }),
    ),
  );

  readonly currentWarehouse = computed(() => this.currentSale()?.almacen?.[0]);
  readonly warehouseName = computed(
    () =>
      this.currentWarehouse()?.nombre ||
      this.translate.instant('global.notes.no-warehouse-assigned'),
  );

  readonly currentArticle = computed<Article | null>(() => {
    const group = this.currentItemGroup();
    return (
      group?.controls.article.value ?? this.currentSale()?.articulo[0] ?? null
    );
  });

  readonly articleName = computed(
    () =>
      this.currentArticle()?.descripcion ||
      this.translate.instant('global.notes.no-description'),
  );
  readonly currentArticleId = computed(() =>
    this.resolveArticleId(this.currentArticle()),
  );
  readonly articleUnitPrice = computed(() => this.currentSale()?.precio || 0);
  readonly articleQuantity = computed(() => this.currentSale()?.cantidad || 0);
  readonly maxAdjustQuantity = computed(() => this.articleQuantity());
  readonly maxAdjustPrice = computed(() => this.articleUnitPrice());

  // Form totals
  private readonly calculateFormArrayTotals = computed(() => {
    this.formArrayVersion();
    return this.calculationService.calculateFormArrayTotals(
      this.itemsForm.controls,
      this.taxIncluded(),
    );
  });

  readonly subtotal = computed(
    () => this.calculateFormArrayTotals().subtotalSum,
  );
  readonly totalWithTax = computed(
    () => this.calculateFormArrayTotals().totalWithTaxSum,
  );
  readonly totalTax = computed(
    () => this.calculateFormArrayTotals().totalTaxSum,
  );

  readonly hasAnyAdjustment = computed(() => {
    this.formArrayVersion();
    return this.validationService.hasAnyAdjustment(this.itemsForm.controls);
  });

  // Type options
  readonly typeOptions = signal<SelectOption[]>([
    {
      value: NoteArticleType.BY_ARTICLE,
      label: this.translate.instant('global.notes.by-article'),
    },
    {
      value: NoteArticleType.BY_TOTAL,
      label: this.translate.instant('global.notes.by-total'),
    },
  ]);

  constructor() {
    this.setupEffects();
    this.setupSubscriptions();
  }

  private setupEffects(): void {
    // Load concepts
    effect(() => {
      const type = this.noteType();
      if (type) this.loadConcepts(type);
    });

    // Concept selection
    effect(() => {
      const conceptValue = this.conceptControl.value;
      if (!conceptValue) return;

      const option = this.conceptOptions().find(
        (o) => o.value === conceptValue,
      );
      if (option) this.selectedConcept.set(option.label);
    });

    // Recreate FormArray only when article type changes
    effect(() => {
      const articleType = this.selectedArticleType();

      this.cleanupLockingSubscriptions();
      this.itemsForm.clear();

      if (articleType === NoteArticleType.BY_TOTAL) {
        this.itemsForm.push(
          this.formManagerService.createEmptyItemGroup(this.invoice()),
        );
      } else {
        this.sales().forEach((sale) =>
          this.itemsForm.push(
            this.formManagerService.createItemGroupFromSale(sale),
          ),
        );
      }

      this.setupMutualLockingEffect();

      if (
        this.noteType() === 'credit' &&
        this.conceptControl.value === this.CANCELLATION_CONCEPT_CODE
      ) {
        this.formManagerService.disableAndSetFullCancellation(
          this.itemsForm.controls,
          this.selectedArticleType(),
          this.invoice(),
          this.sales(),
        );
        this.stateService.incrementFormArrayVersion();
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    this.stateService.searchValue.set(searchTerm);
  }

  private setupSubscriptions(): void {
    // The modal stays open after a note is registered — the host switches to
    // the related notes tab so the new one is visible — so only the form is
    // cleared here. Left filled, it is one "Register" click away from a
    // duplicate note. Closing is the Cancel button's job, not this one's.
    this.createNoteService.noteCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetForm());

    // Article type change
    this.typeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((typeValue) => {
        if (typeValue == null) return;
        const numericValue = Number(typeValue);
        this.stateService.selectedArticleType.set(numericValue);

        if (numericValue === NoteArticleType.BY_TOTAL) {
          this.resetArticleSearch();
        }
      });

    // Concept change - handle cancellation
    this.conceptControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((conceptValue) => {
        const noteType = this.noteType();

        if (
          noteType === 'credit' &&
          conceptValue === this.CANCELLATION_CONCEPT_CODE
        ) {
          this.formManagerService.disableAndSetFullCancellation(
            this.itemsForm.controls,
            this.selectedArticleType(),
            this.invoice(),
            this.sales(),
          );
          this.stateService.incrementFormArrayVersion();
        } else if (conceptValue) {
          this.formManagerService.enableAllControls(this.itemsForm.controls);
        }
      });

    // Article search autocomplete
    this.articleSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap((value) => {
          if (typeof value !== 'string' || !value || value.length < 2) {
            this.stateService.searchedArticles.set([]);
            return of(null);
          }
          return this.inventoryService.searchArticle(value, null, null, 10);
        }),
      )
      .subscribe({
        next: (res) =>
          this.stateService.searchedArticles.set(res?.articles ?? []),
        error: () => this.stateService.searchedArticles.set([]),
      });

    // Form changes
    this.itemsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(50))
      .subscribe(() => {
        this.itemsForm.controls.forEach((group, index) =>
          this.calculationService.recalculateRowTotals(
            group,
            index,
            this.getPriceMode(index),
            this.selectedArticleType(),
            this.invoice(),
            this.sales(),
            this.taxIncluded(),
          ),
        );
        this.stateService.incrementFormArrayVersion();
      });
  }

  private cleanupLockingSubscriptions(): void {
    this.lockingSubscriptions.forEach((sub) => sub.unsubscribe());
    this.lockingSubscriptions = [];
  }

  private setupMutualLockingEffect(): void {
    this.itemsForm.controls.forEach((group, index) => {
      if (this.selectedArticleType() === NoteArticleType.BY_TOTAL) {
        const sub = this.lockingService.setupByTotalPriceValidation(
          group,
          this.invoice()?.venta?.[0]?.total ?? 0,
          () => this.getPriceMode(0),
        );
        this.lockingSubscriptions.push(sub);
      } else {
        const originalSale = this.sales()[index];

        const sub1 = this.lockingService.setupByArticleQuantityLocking(
          group,
          index,
          originalSale?.cantidad ?? 0,
          originalSale?.precio ?? 0,
          (idx) => this.getPriceMode(idx),
          (g, i, p) => this.lockPriceByQuantity(g, i, p),
          (g, i) => this.unlockPrice(g, i),
        );

        const sub2 = this.lockingService.setupByArticlePriceLocking(
          group,
          index,
          originalSale?.cantidad ?? 0,
          originalSale?.precio ?? 0,
          (idx) => this.getPriceMode(idx),
          (idx) => this.stateService.priceLockedByQuantity().has(idx),
          (g, i) => this.unlockQuantityAndReset(g, i),
          (g, i, q) => this.lockQuantityByPrice(g, i, q),
        );

        this.lockingSubscriptions.push(sub1, sub2);
      }
    });
  }

  private lockPriceByQuantity(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalPrice: number,
  ): void {
    const mode = this.getPriceMode(index);
    this.formManagerService.lockPriceByQuantity(
      group,
      index,
      originalPrice,
      mode,
      this.stateService.priceLockedByQuantity,
      this.stateService.quantityLockedByPrice,
    );
  }

  private unlockPrice(group: FormGroup<NoteItemForm>, index: number): void {
    this.formManagerService.unlockPrice(
      group,
      index,
      this.stateService.priceLockedByQuantity,
    );
  }

  private lockQuantityByPrice(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalQty: number,
  ): void {
    this.formManagerService.lockQuantityByPrice(
      group,
      index,
      originalQty,
      this.stateService.quantityLockedByPrice,
    );
  }

  private unlockQuantityAndReset(
    group: FormGroup<NoteItemForm>,
    index: number,
  ): void {
    this.formManagerService.unlockQuantityAndReset(
      group,
      index,
      this.stateService.quantityLockedByPrice,
    );
  }

  private loadConcepts(noteType: NoteType): void {
    this.stateService.isLoadingConcepts.set(true);
    const conceptCode =
      noteType === 'credit' ? NoteConceptCode.CREDIT : NoteConceptCode.DEBIT;

    this.dianService
      .getDianNoteConcepts(conceptCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (concepts) => {
          this.stateService.concepts.set(concepts.data);
          this.stateService.isLoadingConcepts.set(false);
        },
        error: (error) => {
          console.error(
            this.translate.instant('global.notes.error-loading-concepts'),
            error,
          );
          this.stateService.concepts.set([]);
          this.stateService.isLoadingConcepts.set(false);
        },
      });
  }

  getPriceMode(index: number): 'absolute' | 'percentage' {
    return this.stateService.getPriceMode(index);
  }

  getCurrentPriceModeForPage(): 'absolute' | 'percentage' {
    const realIndex = this.getRealIndexForCurrentPage();
    return this.getPriceMode(realIndex >= 0 ? realIndex : 0);
  }

  togglePriceMode(index: number): void {
    const oldMode = this.getPriceMode(index);
    this.stateService.togglePriceMode(index);

    const group = this.itemsForm.at(index);
    if (!group) return;

    const newMode = this.getPriceMode(index);
    const rawValue = Number(group.controls.price.value) || 0;

    const isByTotal = this.selectedArticleType() === NoteArticleType.BY_TOTAL;
    const basePrice = isByTotal
      ? (this.invoice()?.venta?.[0]?.total ?? 0)
      : (this.sales()[index]?.precio ?? 0);

    let newValue = rawValue;

    if (oldMode === 'percentage' && newMode === 'absolute') {
      newValue = (basePrice * rawValue) / 100;
    } else if (oldMode === 'absolute' && newMode === 'percentage') {
      newValue = basePrice ? (rawValue / basePrice) * 100 : 0;
    }

    newValue = Math.round((newValue + Number.EPSILON) * 100) / 100;

    if (newMode === 'percentage') {
      newValue = Math.max(0, Math.min(100, newValue));
    } else {
      newValue = Math.max(0, Math.min(basePrice, newValue));
    }

    group.controls.price.setValue(newValue, { emitEvent: true });

    if (isByTotal && newMode === 'absolute') {
      const invoiceTotal = this.invoice()?.venta?.[0]?.total ?? 0;
      group.controls.price.setValidators([
        Validators.min(0),
        Validators.max(invoiceTotal),
      ]);
      group.controls.price.updateValueAndValidity();
    }
  }

  togglePriceModeForCurrentPage(): void {
    const realIndex = this.getRealIndexForCurrentPage();
    if (realIndex < 0) return;
    this.togglePriceMode(realIndex);
  }

  displayArticleName = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return '';
    return (value as Article).descripcion || '';
  };

  onArticleSelected(article: unknown): void {
    if (!article || typeof article !== 'object') return;
    const art = article as Article;
    this.stateService.selectedArticle.set(art);

    const group = this.itemsForm.at(0);
    if (group) group.controls.article.setValue(art);

    this.articleSearchControl.setValue(art.descripcion, { emitEvent: false });
  }

  onPageChange(page: number): void {
    this.stateService.currentPage.set(page);
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.stateService.itemsPerPage.set(itemsPerPage);
    this.stateService.currentPage.set(0);
  }

  selectSale(index: number): void {
    this.stateService.selectedSaleIndex.set(index);
  }

  private resetArticleSearch(): void {
    this.stateService.resetArticleSearch();
    this.articleSearchControl.setValue('', { emitEvent: false });
  }

  /** Clears the form without touching the modal. */
  private resetForm(): void {
    this.itemsForm.reset();
    this.conceptControl.reset('');
    this.typeControl.setValue(NoteArticleType.BY_ARTICLE);
    this.articleSearchControl.reset('');
    this.stateService.resetState(
      this.translate.instant('global.notes.select-concept-placeholder'),
    );
  }

  onCancel(): void {
    this.resetForm();
    this.closeModal()?.();
  }

  onRegister(): void {
    if (!this.hasAnyAdjustment()) return;

    const invoiceTotal = this.invoice()?.venta?.[0]?.total ?? 0;
    const lineCount = this.itemsForm.controls.filter(
      (g) => (Number(g.controls.quantity.value) || 0) > 0,
    ).length;

    if (
      !this.validationService.validateCreditNoteLimit(
        this.noteType(),
        invoiceTotal,
        this.totalAccredited() ?? 0,
        this.totalWithTax(),
        (amount) => this.calculationService.formatCurrency(amount),
        lineCount,
      )
    )
      return;

    const noteValues = this.builderService.buildNoteValues(
      this.itemsForm.controls,
      this.sales(),
      this.noteType(),
      this.selectedArticleType(),
    );

    // Remaining credit room on the invoice; lets the builder clamp a near-full
    // credit note to the exact remaining so sub-cent recomputation drift never
    // pushes the stored/accumulated total above the invoice.
    const remaining =
      this.noteType() === 'credit'
        ? invoiceTotal - Math.abs(this.totalAccredited() ?? 0)
        : undefined;

    const noteToCreate = this.builderService.buildNoteObject(
      noteValues,
      this.invoice()?.id_factura,
      this.noteType(),
      this.conceptControl.value,
      this.totalWithTax(),
      remaining,
      lineCount,
    );

    this.spinnerService.show();

    const shouldSendDian =
      this.companyFeaturesService.isInternalInvoicingCompany();
    const useExternalInvoicing = this.companyFeaturesService.isBillyCompany();
    const userId = parseInt(this.cookieAuthService.getUserId || '0');

    if (useExternalInvoicing && this.noteType() === 'credit') {
      this.handleExternalInvoicing(noteToCreate, userId);
    } else {
      this.createNoteService.createNote(noteToCreate, userId, shouldSendDian);
    }
  }

  private handleExternalInvoicing(noteToCreate: INotas, userId: number): void {
    this.billyNoteService.sendDian(noteToCreate).then((result) => {
      if (result?.confirm) {
        this.createNoteService.createNote(
          noteToCreate,
          userId,
          false,
          result.data,
        );
      } else {
        this.spinnerService.hide();
      }
    });
  }

  formatCurrency(amount: number): string {
    return this.calculationService.formatCurrency(amount);
  }

  /**
   * `Article.id_articulo` is typed as `string` while
   * `InventoryArticleWarningComponent.articleId` requires a `number` — this
   * coerces and guards against null/undefined/empty/non-numeric values so the
   * warning tag is never instantiated with an invalid id.
   */
  resolveArticleId(article: Article | null | undefined): number | undefined {
    const rawId = article?.id_articulo;
    if (rawId === undefined || rawId === null || rawId === '') {
      return undefined;
    }
    const numericId = Number(rawId);
    return Number.isFinite(numericId) ? numericId : undefined;
  }
}
